import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { FileText, Sparkles, CheckCircle2, AlertTriangle, Lightbulb, Tag } from "lucide-react";

export default function CVReview() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/ai/cv-review");
      setReview(r.data);
      toast.success("CV reviewed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Review failed");
    } finally {
      setLoading(false);
    }
  };

  const scoreColor =
    !review ? "text-gray-300"
    : review.score >= 80 ? "text-[#34C759]"
    : review.score >= 60 ? "text-[#0033FF]"
    : "text-[#FF3B30]";

  return (
    <div className="space-y-10 max-w-4xl" data-testid="cv-review-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ {t("cv_review")}</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">
          We read your CV.
          <br />
          <span className="text-gradient">You fix the weak spots.</span>
        </h1>
        <p className="text-gray-600 max-w-xl">
          Paid recruiters charge SAR 300 for this. You get it instantly — your CV scored 0–100 with actionable rewrites.
        </p>
      </div>

      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center border border-black/10 bg-[#F8F9FA] p-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">Your CV score</div>
          <div className={`num-ticker font-display font-black text-8xl tracking-tighter leading-none ${scoreColor}`}>
            {review ? review.score : "—"}
          </div>
        </div>
        <button
          onClick={run}
          disabled={loading}
          className="bg-black text-white hover:bg-[#0033FF] font-bold px-8 py-5 flex items-center gap-3 transition-colors disabled:opacity-60"
          data-testid="cv-review-run"
        >
          <Sparkles className="w-5 h-5" />
          {loading ? "Analyzing…" : review ? "Re-analyze" : "Run Review"}
        </button>
      </div>

      {!review ? (
        <div className="border border-dashed border-black/20 p-16 text-center text-gray-500" data-testid="cv-review-empty">
          <FileText className="w-12 h-12 mx-auto mb-4 stroke-[1]" />
          Click "Run Review" to evaluate your CV.
          <br />
          <span className="text-xs">Paste your CV text on the Profile page first.</span>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="border border-black/10 bg-white p-8">
            <div className="font-mono text-xs uppercase tracking-widest text-[#0033FF] mb-3">Headline rewrite</div>
            <div className="font-display font-bold text-2xl md:text-3xl tracking-tight">
              "{review.headline_rewrite}"
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-black/10 bg-white p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-[#34C759]" />
                <h3 className="font-display font-bold text-lg">Strengths</h3>
              </div>
              <ul className="space-y-3 text-sm text-gray-800">
                {review.strengths.map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#34C759]">▸</span>{s}</li>
                ))}
              </ul>
            </div>

            <div className="border border-black/10 bg-white p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                <h3 className="font-display font-bold text-lg">Weaknesses</h3>
              </div>
              <ul className="space-y-3 text-sm text-gray-800">
                {review.weaknesses.map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#FF3B30]">▸</span>{s}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border border-black/10 bg-black text-white p-8">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-[#FF3B30]" />
              <h3 className="font-display font-bold text-lg">Suggestions</h3>
            </div>
            <ul className="space-y-3 text-sm">
              {review.suggestions.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-mono text-xs text-[#FF3B30]">{String(i + 1).padStart(2, "0")}</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>

          {review.keywords_missing?.length > 0 && (
            <div className="border border-black/10 bg-white p-6">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-5 h-5" />
                <h3 className="font-display font-bold text-lg">Missing keywords to add</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {review.keywords_missing.map((k) => (
                  <span key={k} className="px-3 py-1 bg-[#F8F9FA] border border-black/10 text-sm">{k}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
